import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  DatePicker,
  Select,
  Button,
  Table,
  InputNumber,
  Space,
  message,
  Divider,
  Typography,
  Row,
  Col,
  Card
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  SaveOutlined,
  ProductOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { Product, Warehouse } from '../types.ts';
import apiClient from '../utils/axiosConfig.ts';
import { generateProductDisplayName } from '../utils/productUtils.ts';
import { formatCurrency } from '../utils/numberFormatting.ts';
import { documentTypeLabel } from '../utils/documentTypes.ts';

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

// Documenti creabili a mano: tutto ciò che nasce in automatico dalle spedizioni
export type ManualDocumentType = 'IM7' | 'IM4' | 'DDT' | 'PACKING_LIST' | 'ALTRO';
export const MANUAL_DOCUMENT_LABELS: Record<ManualDocumentType, string> = {
  IM7: 'H2 (IM7) - Magazzino Doganale',
  IM4: 'H1 (IM4) - Magazzino Nazionale',
  DDT: 'DDT (Magazzino Nazionale)',
  PACKING_LIST: 'Packing List (Magazzino Nazionale)',
  ALTRO: 'Altro (Magazzino Nazionale)',
};

interface ManualCustomsDocumentModalProps {
  visible: boolean;
  documentType?: ManualDocumentType;
  onCancel: () => void;
  onSuccess: () => void;
}

interface ProductLine {
  key: string;
  singolo: number | null;
  product_id: number | null;
  quantity: number;
  unit_cost: number;
  unit_price: number;
  weight_gross: number | null;
  udc_pallet: string;
  udc_collo: string;
  dogana_code: string;
  stock_code: string;
  date_stock: dayjs.Dayjs | null;
  notes: string;
}

interface SupplierOption {
  id: number;
  name: string;
}

interface TransportTypeOption {
  id: number;
  code?: string;
  description: string;
}

interface ShipmentOption {
  id: number;
  shipment_code: string;
  shipment_description?: string;
}

interface ParentDocumentOption {
  id: number;
  document_type: string;
  document_number: string;
}

const ManualCustomsDocumentModal: React.FC<ManualCustomsDocumentModalProps> = ({
  visible,
  documentType = 'IM7',
  onCancel,
  onSuccess
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [suppliers, setSuppliers] = useState<SupplierOption[]>([]);
  const [transportTypes, setTransportTypes] = useState<TransportTypeOption[]>([]);
  const [shipments, setShipments] = useState<ShipmentOption[]>([]);
  const [parentDocuments, setParentDocuments] = useState<ParentDocumentOption[]>([]);
  const [productLines, setProductLines] = useState<ProductLine[]>([
    {
      key: '1',
      singolo: null,
      product_id: null,
      quantity: 0,
      unit_cost: 0,
      unit_price: 0,
      weight_gross: null,
      udc_pallet: '',
      udc_collo: '',
      dogana_code: '',
      stock_code: '',
      date_stock: null,
      notes: ''
    }
  ]);

  useEffect(() => {
    if (visible) {
      loadInitialData();
      // il tipo arriva dal pulsante premuto (Nuovo IM7, Nuovo IM4, Nuovo DDT / Packing List / Altro)
      form.setFieldsValue({ document_type: documentType });
    }
  }, [visible, documentType]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadInitialData = async () => {
    try {
      const [
        productsResponse,
        warehousesResponse,
        suppliersResponse,
        transportTypesResponse,
        shipmentsResponse,
        parentDocsResponse
      ] = await Promise.all([
        apiClient.get('/products'),
        apiClient.get('/warehouses'),
        apiClient.get('/suppliers'),
        apiClient.get('/transport-types/'),
        apiClient.get('/unified-shipments/', { params: { limit: 1000 } }),
        apiClient.get('/customs-documents', { params: { limit: 1000 } })
      ]);

      setProducts(productsResponse.data);
      setWarehouses(warehousesResponse.data);
      setSuppliers(suppliersResponse.data || []);
      setTransportTypes(transportTypesResponse.data || []);
      setShipments(shipmentsResponse.data?.shipments || []);
      setParentDocuments(parentDocsResponse.data?.documents || []);
    } catch (error) {
      message.error('Errore nel caricamento dei dati iniziali');
      console.error('Error loading initial data:', error);
    }
  };

  const addProductLine = () => {
    const newKey = (productLines.length + 1).toString();
    setProductLines([
      ...productLines,
      {
        key: newKey,
        singolo: null,
        product_id: null,
        quantity: 0,
        unit_cost: 0,
        unit_price: 0,
        weight_gross: null,
        udc_pallet: '',
        udc_collo: '',
        dogana_code: '',
        stock_code: '',
        date_stock: null,
        notes: ''
      }
    ]);
  };

  const removeProductLine = (key: string) => {
    if (productLines.length > 1) {
      setProductLines(productLines.filter(line => line.key !== key));
    }
  };

  const updateProductLine = (key: string, field: keyof ProductLine, value: any) => {
    setProductLines(productLines.map(line =>
      line.key === key ? { ...line, [field]: value } : line
    ));
  };

  const handleProductSelect = (key: string, productId: number | null) => {
    const selectedProduct = productId ? products.find(p => p.id === productId) : null;
    setProductLines(productLines.map(line =>
      line.key === key
        ? {
            ...line,
            product_id: productId,
            dogana_code: selectedProduct?.dogana_code || '',
            unit_cost: selectedProduct?.cost ?? 0,
            unit_price: selectedProduct?.price ?? 0,
            weight_gross: selectedProduct?.weight_gross ?? null
          }
        : line
    ));
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      // Validazione righe prodotto
      const validProductLines = productLines.filter(line => 
        line.product_id && line.quantity > 0
      );

      if (validProductLines.length === 0) {
        message.error('Inserire almeno un prodotto con quantità maggiore di zero');
        return;
      }

      // Preparazione dati per API
      const submitData = {
        document_type: values.document_type,
        document_number: values.document_number,
        document_date: values.document_date.format('YYYY-MM-DD'),
        warehouse_id: values.warehouse_id,
        reason: values.reason || 'Caricamento storico manuale',
        notes: values.notes,
        supplier_id: values.supplier_id || null,
        shipment_id: values.shipment_id || null,
        transport_type_id: values.transport_type_id || null,
        origin_location: values.origin_location || null,
        destination_location: values.destination_location || null,
        parent_document_id: values.parent_document_id || null,
        mnr_code: values.mnr_code || null,
        products: validProductLines.map(line => ({
          product_id: line.product_id,
          quantity: line.quantity,
          unit_cost: line.unit_cost || 0,
          unit_price: line.unit_price || 0,
          singolo: line.singolo || null,
          weight_gross: line.weight_gross || null,
          udc_pallet: line.udc_pallet || '',
          udc_collo: line.udc_collo || '',
          dogana_code: line.dogana_code || null,
          stock_code: line.stock_code || null,
          date_stock: line.date_stock ? line.date_stock.format('YYYY-MM-DD HH:mm:ss') : null,
          notes: line.notes || null
        }))
      };

      setLoading(true);

      const response = await apiClient.post('/customs-documents/manual', submitData);
      
      message.success(response.data?.message || `Documento ${values.document_type} ${values.document_number} creato in bozza`, 8);
      
      form.resetFields();
      setProductLines([{
        key: '1',
        singolo: null,
        product_id: null,
        quantity: 0,
        unit_cost: 0,
        unit_price: 0,
        weight_gross: null,
        udc_pallet: '',
        udc_collo: '',
        dogana_code: '',
        stock_code: '',
        date_stock: null,
        notes: ''
      }]);
      
      onSuccess();
      
    } catch (error: any) {
      console.error('Error creating manual customs document:', error);
      if (error.response?.data?.detail) {
        message.error(`Errore: ${error.response.data.detail}`);
      } else {
        message.error('Errore durante il caricamento del documento doganale');
      }
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Singolo',
      dataIndex: 'singolo',
      key: 'singolo',
      width: '10%',
      render: (value: number | null, record: ProductLine) => (
        <InputNumber
          min={0}
          value={value}
          onChange={(val) => updateProductLine(record.key, 'singolo', val || null)}
          style={{ width: '100%' }}
          placeholder="Opz."
        />
      )
    },
    {
      title: 'Prodotto',
      dataIndex: 'product_id',
      key: 'product_id',
      width: '25%',
      render: (value: number | null, record: ProductLine) => (
        <Select
          placeholder="Seleziona prodotto"
          value={value}
          onChange={(productId) => handleProductSelect(record.key, productId)}
          showSearch
          optionFilterProp="children"
          style={{ width: '100%' }}
        >
          {products.map(product => (
            <Option key={product.id} value={product.id}>
              {generateProductDisplayName(
                product.internal_product_code,
                product.size,
                product.color,
                product.product_description
              )}
            </Option>
          ))}
        </Select>
      )
    },
    {
      title: 'Quantità',
      dataIndex: 'quantity',
      key: 'quantity',
      width: '12%',
      render: (value: number, record: ProductLine) => (
        <InputNumber
          min={0}
          value={value}
          onChange={(qty) => updateProductLine(record.key, 'quantity', qty || 0)}
          style={{ width: '100%' }}
        />
      )
    },
    {
      title: 'Costo Unitario',
      dataIndex: 'unit_cost',
      key: 'unit_cost',
      width: '15%',
      render: (value: number, record: ProductLine) => (
        <InputNumber
          min={0}
          step={0.01}
          value={value}
          onChange={(cost) => updateProductLine(record.key, 'unit_cost', cost || 0)}
          style={{ width: '100%' }}
          addonAfter="€"
        />
      )
    },
    {
      title: 'Vendita Unitario',
      dataIndex: 'unit_price',
      key: 'unit_price',
      width: '12%',
      render: (value: number, record: ProductLine) => (
        <InputNumber
          min={0}
          step={0.01}
          value={value}
          onChange={(price) => updateProductLine(record.key, 'unit_price', price || 0)}
          style={{ width: '100%' }}
          addonAfter="€"
        />
      )
    },
    {
      title: 'Peso Lordo',
      dataIndex: 'weight_gross',
      key: 'weight_gross',
      width: '12%',
      render: (value: number | null, record: ProductLine) => (
        <InputNumber
          min={0}
          step={0.0001}
          precision={4}
          value={value}
          onChange={(peso) => updateProductLine(record.key, 'weight_gross', peso || null)}
          style={{ width: '100%' }}
          addonAfter="kg"
          placeholder="Opz."
        />
      )
    },
    {
      title: 'UDC Pallet',
      dataIndex: 'udc_pallet',
      key: 'udc_pallet',
      width: '10%',
      render: (value: string, record: ProductLine) => (
        <Input
          placeholder="Pallet (opz.)"
          value={value}
          onChange={(e) => updateProductLine(record.key, 'udc_pallet', e.target.value)}
        />
      )
    },
    {
      title: 'UDC Collo',
      dataIndex: 'udc_collo',
      key: 'udc_collo',
      width: '10%',
      render: (value: string, record: ProductLine) => (
        <Input
          placeholder="Collo (opz.)"
          value={value}
          onChange={(e) => updateProductLine(record.key, 'udc_collo', e.target.value)}
        />
      )
    },
    {
      title: 'Codice Doganale',
      dataIndex: 'dogana_code',
      key: 'dogana_code',
      width: 140,
      render: (value: string, record: ProductLine) => (
        <Input
          placeholder="Cod. dog. (opz.)"
          value={value}
          onChange={(e) => updateProductLine(record.key, 'dogana_code', e.target.value)}
        />
      )
    },
    {
      title: 'Stock Code',
      dataIndex: 'stock_code',
      key: 'stock_code',
      width: 120,
      render: (value: string, record: ProductLine) => (
        <Input
          placeholder="Stock (opz.)"
          value={value}
          onChange={(e) => updateProductLine(record.key, 'stock_code', e.target.value)}
        />
      )
    },
    {
      title: 'Data Stock',
      dataIndex: 'date_stock',
      key: 'date_stock',
      width: 150,
      render: (value: dayjs.Dayjs | null, record: ProductLine) => (
        <DatePicker
          value={value}
          onChange={(date) => updateProductLine(record.key, 'date_stock', date)}
          format="DD/MM/YYYY"
          style={{ width: '100%' }}
          placeholder="Opz."
        />
      )
    },
    {
      title: 'Note',
      dataIndex: 'notes',
      key: 'notes',
      width: 160,
      render: (value: string, record: ProductLine) => (
        <Input
          placeholder="Note (opz.)"
          value={value}
          onChange={(e) => updateProductLine(record.key, 'notes', e.target.value)}
          className="no-uppercase-modal"
        />
      )
    },
    {
      title: 'Azioni',
      key: 'actions',
      width: '6%',
      render: (_, record: ProductLine) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeProductLine(record.key)}
          disabled={productLines.length <= 1}
        />
      )
    }
  ];

  const getTotalQuantity = () => {
    return productLines.reduce((sum, line) => sum + (line.quantity || 0), 0);
  };

  const getTotalValue = () => {
    return productLines.reduce((sum, line) => 
      sum + ((line.quantity || 0) * (line.unit_cost || 0)), 0
    );
  };

  return (
    <Modal
      title={
        <Space>
          <ProductOutlined />
          <span>Caricamento Manuale Documento di Carico</span>
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      width="95vw"
      style={{ top: 20 }}
      styles={{ body: { maxHeight: 'calc(100vh - 180px)', overflowY: 'auto' } }}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Annulla
        </Button>,
        <Button
          key="submit"
          type="primary"
          icon={<SaveOutlined />}
          loading={loading}
          onClick={handleSubmit}
        >
          Carica Documento
        </Button>
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          document_type: documentType,
          document_date: dayjs(),
          reason: 'Caricamento storico manuale'
        }}
      >
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item
              name="document_type"
              label="Tipo Documento"
              rules={[{ required: true, message: 'Seleziona il tipo documento' }]}
              tooltip={documentType === 'IM7' ? 'Caricamento in magazzino doganale' : 'Caricamento in magazzino nazionale'}
            >
              <Select disabled={documentType === 'IM7' || documentType === 'IM4'}>
                {(documentType === 'IM7' || documentType === 'IM4'
                  ? [documentType]
                  : (['DDT', 'PACKING_LIST', 'ALTRO'] as ManualDocumentType[])
                ).map((t) => (
                  <Option key={t} value={t}>{MANUAL_DOCUMENT_LABELS[t]}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          
          <Col span={6}>
            <Form.Item
              name="document_number"
              label="Numero Documento"
              rules={[{ required: true, message: 'Inserire il numero documento' }]}
            >
              <Input placeholder="es. DOC001" />
            </Form.Item>
          </Col>
          
          <Col span={6}>
            <Form.Item
              name="document_date"
              label="Data Documento"
              rules={[{ required: true, message: 'Selezionare la data' }]}
            >
              <DatePicker
                style={{ width: '100%' }}
                format="DD-MM-YYYY"
              />
            </Form.Item>
          </Col>
          
          <Col span={6}>
            <Form.Item
              name="warehouse_id"
              label="Magazzino"
              rules={[{ required: true, message: 'Seleziona il magazzino' }]}
            >
              <Select placeholder="Seleziona magazzino">
                {warehouses
                  .filter(warehouse => documentType === 'IM7' ? !warehouse.is_nationalized : warehouse.is_nationalized)
                  .map(warehouse => (
                    <Option key={warehouse.id} value={warehouse.id}>
                      {warehouse.cod_mag ? `${warehouse.cod_mag} - ${warehouse.description}` : warehouse.description}
                      {' '}({warehouse.is_nationalized ? 'Nazionale' : 'Doganale'})
                    </Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={6}>
            <Form.Item
              name="supplier_id"
              label="Fornitore (opzionale)"
            >
              <Select
                placeholder="Seleziona fornitore"
                showSearch
                allowClear
                optionFilterProp="children"
              >
                {suppliers.map(s => (
                  <Option key={s.id} value={s.id}>{s.name}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={6}>
            <Form.Item
              name="shipment_id"
              label="Spedizione (opzionale)"
            >
              <Select
                placeholder="Seleziona spedizione"
                showSearch
                allowClear
                optionFilterProp="children"
              >
                {shipments.map(s => (
                  <Option key={s.id} value={s.id}>
                    {s.shipment_code}{s.shipment_description ? ` - ${s.shipment_description}` : ''}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={6}>
            <Form.Item
              name="transport_type_id"
              label="Tipo Trasporto (opzionale)"
            >
              <Select
                placeholder="Seleziona tipo trasporto"
                showSearch
                allowClear
                optionFilterProp="children"
              >
                {transportTypes.map(t => (
                  <Option key={t.id} value={t.id}>
                    {t.code ? `${t.code} - ${t.description}` : t.description}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={6}>
            <Form.Item
              name="mnr_code"
              label="Codice MNR (opzionale)"
            >
              <Input placeholder="es. MNR123" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="origin_location"
              label="Origine (opzionale)"
            >
              <Input placeholder="Località di origine" />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name="destination_location"
              label="Destinazione (opzionale)"
            >
              <Input placeholder="Località di destinazione" />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name="parent_document_id"
              label="Documento Padre (opzionale)"
            >
              <Select
                placeholder="Seleziona documento padre"
                showSearch
                allowClear
                optionFilterProp="children"
              >
                {parentDocuments.map(d => (
                  <Option key={d.id} value={d.id}>
                    {documentTypeLabel(d.document_type)} - {d.document_number}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="reason"
              label="Motivo Caricamento"
            >
              <Input placeholder="Caricamento storico manuale" />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="notes"
              label="Note (opzionale)"
            >
              <TextArea rows={2} placeholder="Note aggiuntive..." />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Divider>Prodotti da Caricare</Divider>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={12}>
            <Text strong>Totale Prodotti: </Text>
            <Text>{productLines.filter(line => line.product_id).length}</Text>
          </Col>
          <Col span={12}>
            <Text strong>Quantità Totale: </Text>
            <Text>{getTotalQuantity()}</Text>
            <Divider type="vertical" />
            <Text strong>Valore Totale: </Text>
            <Text>€ {formatCurrency(getTotalValue())}</Text>
          </Col>
        </Row>
      </Card>

      <Table
        columns={columns}
        dataSource={productLines}
        pagination={false}
        size="small"
        scroll={{ x: 1800, y: 'calc(100vh - 580px)' }}
        footer={() => (
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={addProductLine}
            style={{ width: '100%' }}
          >
            Aggiungi Prodotto
          </Button>
        )}
      />
    </Modal>
  );
};

export default ManualCustomsDocumentModal;